/* @jsx h */
import { defineComponent, ref, h, onMounted, computed } from "vue";
import { setTitle } from "@lemmonhq/orders";
import {
  SectionedBody,
  type SectionedBodySection,
  useLocalization,
} from "@lemmonhq/uikit";
import { api } from "@app/data/index";
import { useToast } from "@app/vue/providers/Toast";
import { VacationScheduleComponent } from "./ConfigSettings/VacationSchedule";
import { NotificationsSettingsComponent } from "./ConfigSettings/NotificationsSettings";
import { AppSubpage, AppSubpageContent } from "@app/vue/components/AppSubpage";
import { AppSubpageHeader } from "@app/vue/components/AppSubpageHeader";
import { ConfigSettingsLoading } from "./ConfigSettings/ConfigSettingsLoading";
import { MobilePosSettingsCard } from "./ConfigSettings/MobilePosSettingsCard";
import { TipsSettingsCard } from "./ConfigSettings/TipsSettingsCard";
import { OperatingHoursCard } from "./ConfigSettings/OperatingHoursCard";
import { ServiceOptionsCard } from "./ConfigSettings/ServiceOptionsCard";
import { TableOrderSettingsCard } from "./ConfigSettings/TableOrderSettingsCard";
import {
  createDefaultConfigSettingsForm,
  createSettingsPayload,
  parseSettings,
  serializeConfig,
} from "./ConfigSettings/settingsForm";
import type { ConfigSettingsFormState } from "./ConfigSettings/types";
import { useAuth } from "@app/vue/providers/Auth";

export const ConfigSettings = defineComponent({
  name: "ConfigSettings",
  setup() {
    const auth = useAuth();
    const { t } = useLocalization();
    const loading = ref(true);
    const saving = ref(false);
    const errorMessage = ref<string | null>(null);
    const form = ref<ConfigSettingsFormState>(
      createDefaultConfigSettingsForm(),
    );
    const serializedInitial = ref<string>(serializeConfig(form.value));
    const toast = useToast();

    const sections = computed<SectionedBodySection[]>(() => [
      {
        id: "operating-hours",
        label: t("settings.config.operating_hours"),
        icon: "schedule",
      },
      {
        id: "service-options",
        label: t("settings.config.service_options"),
        icon: "room_service",
      },
      {
        id: "table-orders",
        label: t("settings.config.table_orders"),
        icon: "table_restaurant",
      },
      {
        id: "mobile-pos",
        label: t("settings.config.mobile_pos"),
        icon: "smartphone",
      },
      {
        id: "tips",
        label: t("settings.config.tip_suggestions"),
        icon: "payments",
      },
      {
        id: "notifications",
        label: t("settings.notifications.title"),
        icon: "notifications",
      },
      {
        id: "vacation-schedule",
        label: t("settings.config.vacation_schedule"),
        icon: "beach_access",
      },
    ]);

    const isDirty = computed(
      () => serializeConfig(form.value) !== serializedInitial.value,
    );

    const loadSettings = async () => {
      loading.value = true;
      errorMessage.value = null;
      try {
        const response = await api.getSettings();
        const config = parseSettings(response.data);
        form.value = config;
        serializedInitial.value = serializeConfig(config);
      } catch (error) {
        console.error("Failed to load restaurant settings", error);
        errorMessage.value = t("settings.config.load_error");
      } finally {
        loading.value = false;
      }
    };

    const resetForm = () => {
      loadSettings();
    };

    const handleSave = async () => {
      if (saving.value) return;
      saving.value = true;
      errorMessage.value = null;

      try {
        await api.bulkUpdateSettings(
          {
            settings: createSettingsPayload(form.value),
          },
          auth,
        );

        const response = await api.getSettings();
        const updatedConfig = parseSettings(response.data);
        form.value = updatedConfig;
        serializedInitial.value = serializeConfig(updatedConfig);

        toast.showToast(t("settings.config.save_success"), {
          type: "success",
          autoHide: 4000,
        });
      } catch (error) {
        console.error("Failed to save restaurant settings", error);
        errorMessage.value = t("settings.config.save_error");
        toast.showToast(t("settings.config.save_error_toast"), {
          type: "error",
        });
      } finally {
        saving.value = false;
      }
    };

    onMounted(() => {
      loadSettings();
    });

    return () => (
      <div class="flex flex-1 flex-col">
        <AppSubpageHeader
          icon="tune"
          title={t("settings.config.title")}
          description={t("settings.config.description")}
        >
          {{
            actions: () => (
              <>
                <button
                  type="button"
                  class="btn btn-ghost btn-sm"
                  disabled={!isDirty.value || saving.value}
                  onClick={resetForm}
                >
                  {t("settings.config.reset")}
                </button>
                <button
                  type="button"
                  class={[
                    "btn btn-primary btn-sm gap-2 transition-all shadow-lg hover:shadow-primary/25",
                    saving.value ? "btn-disabled" : "",
                  ].join(" ")}
                  disabled={!isDirty.value || saving.value}
                  onClick={handleSave}
                >
                  {saving.value ? (
                    <span class="loading loading-spinner loading-sm"></span>
                  ) : (
                    <span class="material-symbols-outlined text-lg">save</span>
                  )}
                  {saving.value
                    ? t("settings.config.saving")
                    : t("settings.config.save_changes")}
                </button>
              </>
            ),
          }}
        </AppSubpageHeader>

        <AppSubpage>
          <AppSubpageContent noPadding class="p-0 pb-4 lg:p-6">
            {errorMessage.value ? (
              <div class="alert alert-error mb-6 border border-error/30 bg-error/10 text-error-content shadow-lg">
                <span class="material-symbols-outlined text-lg">error</span>
                <span>{errorMessage.value}</span>
              </div>
            ) : null}

            {loading.value ? (
              <ConfigSettingsLoading />
            ) : (
              <SectionedBody
                sections={sections.value}
                ariaLabel={t("settings.config.title")}
              >
                {{
                  "operating-hours": () => (
                    <OperatingHoursCard
                      startTime={form.value.start_time}
                      endTime={form.value.end_time}
                      onUpdateStartTime={(value) =>
                        (form.value.start_time = value)
                      }
                      onUpdateEndTime={(value) => (form.value.end_time = value)}
                    />
                  ),
                  "service-options": () => (
                    <ServiceOptionsCard
                      takeAway={form.value.take_away}
                      delivery={form.value.delivery}
                      onUpdateTakeAway={(value) =>
                        (form.value.take_away = value)
                      }
                      onUpdateDelivery={(value) =>
                        (form.value.delivery = value)
                      }
                    />
                  ),
                  "table-orders": () => (
                    <TableOrderSettingsCard
                      reduceQuantitiesUpdateKitchenDefault={
                        form.value.reduce_quantities_update_kitchen_default
                      }
                      onUpdateReduceQuantitiesUpdateKitchenDefault={(value) =>
                        (form.value.reduce_quantities_update_kitchen_default =
                          value)
                      }
                      allowOrdersExceedingTableCredit={
                        form.value.allow_orders_exceeding_table_credit
                      }
                      onUpdateAllowOrdersExceedingTableCredit={(value) =>
                        (form.value.allow_orders_exceeding_table_credit = value)
                      }
                      giftCardAmounts={form.value.gift_card_amounts}
                      onUpdateGiftCardAmounts={(value) =>
                        (form.value.gift_card_amounts = value)
                      }
                    />
                  ),
                  "mobile-pos": () => (
                    <MobilePosSettingsCard
                      orderTypes={form.value.mobile_pos_order_types}
                      onUpdateOrderTypes={(orderTypes) =>
                        (form.value.mobile_pos_order_types = orderTypes)
                      }
                    />
                  ),
                  tips: () => (
                    <TipsSettingsCard
                      recommendedTips={form.value.recommended_tips}
                      onUpdate={(value) =>
                        (form.value.recommended_tips = value)
                      }
                    />
                  ),
                  notifications: () => (
                    <NotificationsSettingsComponent
                      settings={form.value.notification_settings}
                      onUpdate={(settings) =>
                        (form.value.notification_settings = settings)
                      }
                    />
                  ),
                  "vacation-schedule": () => (
                    <VacationScheduleComponent
                      schedule={form.value.vacation_schedule}
                      onUpdate={(schedule) =>
                        (form.value.vacation_schedule = schedule)
                      }
                    />
                  ),
                }}
              </SectionedBody>
            )}
          </AppSubpageContent>
        </AppSubpage>
      </div>
    );
  },
});
